import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, FileText, ArrowRight, BadgeCheck } from 'lucide-react';

export const CTASection: React.FC = () => {
  return (
    <section className="py-32 px-6 relative z-10 flex justify-center">
      {/* Instead of floating text, the entire CTA is housed in a massive 
        frosted-glass execution block to anchor the bottom of the page.
      */}
      <div className="max-w-6xl w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, type: "spring", bounce: 0.1 }}
          className="relative rounded-[3rem] border border-white/10 bg-black/40 backdrop-blur-2xl p-10 md:p-20 overflow-hidden text-center shadow-2xl"
        >
          {/* Ambient inner glows */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-[#00ff9d]/50 to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00ff9d]/10 blur-[120px] rounded-full pointer-events-none" />
          
          {/* Subtle terminal grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none" />

          {/* Content Wrapper */}
          <div className="relative z-10">
            {/* Early access badge */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 1, delay: 0.2 }}
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#00ff9d]/30 bg-[#00ff9d]/10 backdrop-blur-md mb-10 shadow-[0_0_20px_rgba(0,255,157,0.15)]"
            >
              <BadgeCheck className="w-4 h-4 text-[#00ff9d]" />
              <span className="text-[#00ff9d] text-xs font-bold uppercase tracking-[0.15em]">
                Early access opens soon
              </span>
            </motion.div>

            {/* Main headline */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/60"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Claim your amPassport.
              <br />
              Carry your reputation everywhere.
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg md:text-xl text-white/60 mb-14 max-w-2xl mx-auto leading-relaxed font-light"
            >
              Join early access to mint your soulbound passport and unlock portable, verifiable reputation.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-5 justify-center mb-20"
            >
              {/* Primary CTA (Disabled/Locked State) */}
              <button
                disabled
                className="group relative px-10 py-5 rounded-2xl font-bold text-base overflow-hidden bg-white/5 border border-white/10 text-white/40 cursor-not-allowed backdrop-blur-sm transition-all"
              >
                {/* Diagonal striping for disabled look */}
                <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(255,255,255,0.02)_10px,rgba(255,255,255,0.02)_20px)] pointer-events-none" />
                
                <div className="relative z-10 flex items-center justify-center gap-3 opacity-70">
                  <Rocket className="w-5 h-5" />
                  <span>Request access</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>

              {/* Secondary CTA */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-5 rounded-2xl font-semibold text-base bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 hover:border-[#00ff9d]/50 text-white transition-all flex items-center justify-center gap-3 shadow-lg"
              >
                <FileText className="w-5 h-5 opacity-70" />
                <span>Read Docs</span>
              </motion.button>
            </motion.div>

            {/* Value grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
            >
              {[
                { value: "Always on-chain", label: "Permanent proofs" },
                { value: "Multi-source", label: "Wallets + socials" },
                { value: "Portable", label: "One profile everywhere" },
                { value: "Verifiable", label: "Open reputation graph" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="p-5 rounded-2xl bg-black/40 border border-white/5 hover:border-[#00ff9d]/30 hover:bg-[#00ff9d]/5 transition-colors group relative overflow-hidden"
                >
                  {/* Subtle top highlight */}
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 group-hover:via-[#00ff9d]/40 to-transparent transition-colors" />
                  
                  <div className="text-sm font-bold text-[#00ff9d] mb-1.5 tracking-wide">
                    {stat.value}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.1em] text-white/50 font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Bottom text */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1 }}
              className="mt-16 flex items-center justify-center gap-3 text-white/40 text-[11px] uppercase tracking-[0.1em] font-semibold"
            >
              <div className="w-8 h-px bg-white/10" />
              <p>No wallets required to join waitlist • Free for early adopters • Built on Arweave</p>
              <div className="w-8 h-px bg-white/10" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
